import * as Styled from './LeftForm.styles';

import { allowsDecimals, allowsOnlyNumbers } from 'utils/numberInput';

import { Form } from 'features/common/components/Form';
import { Option } from 'features/common/components/Form/types';
import { Pool } from '../../types';
import { PunchupButton } from 'features/common/components/Form/PunchupButton/PunchupButton';
import { PunchupDateTimePicker } from 'features/common/components/Form/PunchupDateTimePicker/PunchupDateTimePicker';
import { PunchupToggle } from 'features/common/components/Form/PunchupToggle/PunchupToggle';
import { TicketTagsField } from '../TicketTagsField';
import { PunchupColorPicker } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupColorPicker';
import { TicketImageCard } from 'features/Dashboard/components/TicketsSection/TicketCard/TicketImageCard';
import { convertUTCToDate } from 'utils/utils';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTicketTypeApi } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { TicketDetailsParams, TicketBody } from '../../types';
import { airbrake } from 'ErrorBoundary';
import { SeatsCategories } from './SeatsCategories';
import { SeatsIOCategory } from 'services/Seats';
import { FlexContainer } from 'features/common/components/FlexContainer';
import { useMutation } from '@tanstack/react-query';
import { deleteTicket } from 'features/PunchupTicketDetails/services';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PRIMARY, SECONDARY } from 'features/common/components/Button/constants';

interface LeftFormProps {
	isNewTicket: boolean;
	ticket?: TicketBody & { sold?: number };
}

// eslint-disable-next-line react/prop-types
const LeftForm: React.FC<LeftFormProps> = ({ isNewTicket, ticket: ticketProp }) => {
	// All hooks at the top, in the same order, unconditionally
	const { t } = useTranslation();
	const { event } = useGetEvent();
	const { pools, ticket, mutateUploadImage } = useTicketTypeApi();
	const { watch, setValue } = useFormContext();
	const { id: eventId, ticketId } = useParams<TicketDetailsParams>();
	const history = useHistory();

	// All useState hooks
	const [initialLoad, setInitialLoad] = useState(true);
	const [previousPrivateLink, setPreviousPrivateLink] = useState(false);
	const [copiedToClipboard, setCopiedToClipboard] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	// All watched values
	const privateLink: boolean = watch('privateLink');
	const hiddenCode: string = watch('hiddenCode');
	const showImage: boolean = watch('showImage');
	const ticketImage: string | File | Blob = watch('ticketImage');
	const rawSeatsCategoryKey = watch('seatsCategoryKey');
	const seatsCategoryKey: string | undefined = rawSeatsCategoryKey ? String(rawSeatsCategoryKey) : undefined;
	const payWhatYouWant: boolean = watch('payWhatYouWant');
	const color: string = watch('color');

	const activeTicket = ticketProp || ticket;
	const hasValidImage = ticketImage !== undefined && ticketImage !== '';
	const hasSoldTickets = activeTicket?.sold && !isNaN(activeTicket.sold) && activeTicket.sold > 0;

	// Delete mutation
	const { mutateAsync: mutateDeleteTicket } = useMutation(deleteTicket, {
		onSuccess: () => {
			toast.success('Ticket type deleted successfully');
			history.goBack();
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message || 'Failed to delete ticket type');
		},
	});

	const generateHexCode = (): string => {
		return Array.from({ length: 22 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
	};

	const getWidgetUrl = (code: string): string => {
		const widgetBase = process.env.REACT_APP_WIDGET;
		return `${widgetBase}/event/${eventId}/tickets?code=${code}`;
	};

	const handleCopyToClipboard = async (text: string) => {
		try {
			window.parent.postMessage({ action: 'copy-to-clipboard', text }, '*');
			setCopiedToClipboard(true);
			setTimeout(() => setCopiedToClipboard(false), 2000);
		} catch {
			// Silent fail
		}
	};

	const handleImageDisplay = () => {
		if (ticketImage instanceof Blob) return URL.createObjectURL(ticketImage);
		return ticketImage;
	};

	const convertFileToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});
	};

	const handleImageUpload = () => {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = 'image/jpeg, image/png';
		input.onchange = async (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) {
				try {
					const base64String = await convertFileToBase64(file);

					const imageData = {
						url: URL.createObjectURL(file),
						base64: base64String,
						name: file.name,
					};

					const response = await mutateUploadImage({
						image: imageData,
						prefix: `${eventId}-${watch('name') || 'ticket'}`,
						index: 0,
						type: 'template',
					});

					setValue('image', response.data.image_url);
					setValue('ticketImage', response.data.image_url);
					setValue('displayType', 'image');
				} catch (error) {
					airbrake.notify(error);
				}
			}
		};
		input.click();
	};

	const handleReplaceImage = () => {
		handleImageUpload();
	};

	const handleDeleteImage = () => {
		setValue('ticketImage', '');
		setValue('image', '');
		setValue('displayType', '');
	};

	const handleCategorySelect = (category: SeatsIOCategory) => {
		const categoryKeyStr = String(category.key);
		if (seatsCategoryKey === categoryKeyStr) {
			setValue('seatsCategoryKey', undefined);
		} else {
			setValue('seatsCategoryKey', categoryKeyStr);
		}
	};

	useEffect(() => {
		if (event?.endDate && event?.timeZone && !watch('offSellingTime')) {
			const offSellingTime = moment(convertUTCToDate(`${event.endDate}`, event.timeZone));
			setValue('offSellingTime', offSellingTime);
		}
	}, [event]);

	useEffect(() => {
		if (initialLoad && hiddenCode !== undefined) {
			setInitialLoad(false);
		}
	}, [hiddenCode, initialLoad]);

	useEffect(() => {
		if (!initialLoad && privateLink !== previousPrivateLink) {
			if (privateLink && !hiddenCode) {
				const newCode = generateHexCode();
				setValue('hiddenCode', newCode);
				setValue('ticketVisibility', false);
			} else if (!privateLink && hiddenCode) {
				setValue('hiddenCode', null);
			}
		}
		setPreviousPrivateLink(privateLink);
	}, [privateLink, hiddenCode, setValue, initialLoad, previousPrivateLink]);

	useEffect(() => {
		if (!payWhatYouWant) {
			setValue('minPrice', 0);
			setValue('maxPrice', 0);
		} else {
			setValue('initialPrice', 0);
		}
	}, [payWhatYouWant]);

	const handleDelete = async () => {
		if (ticketId) {
			await mutateDeleteTicket(Number(ticketId));
		}
	};

	const handleCancelDelete = () => {
		setShowDeleteConfirm(false);
	};

	const poolOptions: Option[] =
		pools?.map((pool: Pool) => ({
			label: pool.name,
			value: pool.ID,
		})) || [];

	return (
		<>
			<Form.PunchupInput id="name" name="name" label={t('ticketDetails-ticket-type-name')} />

			<FlexContainer gap="8px" flexDirection="column">
				<Styled.Label>Ticket Color (Optional)</Styled.Label>
				<PunchupColorPicker value={color || ''} onChange={(newColor) => setValue('color', newColor)} />
			</FlexContainer>

			<Styled.WrapperInputs>
				<PunchupToggle htmlFor="payWhatYouWant" name="payWhatYouWant" label="Pay what you want ticket" />
			</Styled.WrapperInputs>

			<Styled.WrapperInputs>
				{payWhatYouWant ? (
					<>
						<Form.PunchupInput
							type="number"
							id="minPrice"
							name="minPrice"
							label={t('ticketDetails-min-ticket-price')}
							onKeyDown={allowsDecimals}
							min={0}
							step=".01"
						/>
						<Form.PunchupInput
							type="number"
							id="maxPrice"
							name="maxPrice"
							label={t('ticketDetails-max-ticket-price')}
							onKeyDown={allowsDecimals}
							min={0}
							step=".01"
						/>
					</>
				) : (
					<>
						<Form.PunchupInput
							type="number"
							id="initialPrice"
							name="initialPrice"
							label={t('ticketDetails-ticket-price')}
							onKeyDown={allowsDecimals}
							min={0}
							step=".01"
						/>
						<Form.PunchupInput
							type="number"
							id="percentageFee"
							name="percentageFee"
							label={t('ticketDetails-percentage-custom-fee')}
							onKeyDown={allowsDecimals}
							min={0}
							step=".01"
						/>
						<Form.PunchupInput
							type="number"
							id="fixedFee"
							name="fixedFee"
							label={t('ticketDetails-fixed-custom-fee')}
							onKeyDown={allowsDecimals}
							min={0}
							step=".01"
						/>
					</>
				)}
			</Styled.WrapperInputs>

			<Styled.WrapperInputs>
				<Form.PunchupInput
					type="number"
					id="amount"
					name="amount"
					label={t('ticketDetails-quantity')}
					onKeyDown={allowsOnlyNumbers}
					min={0}
					step={1}
				/>
				<Form.PunchupInput
					type="number"
					id="maxQuantity"
					name="maxQuantity"
					label={`${t('ticketDetails-max-quantity')} (${t('optional')})`}
					onKeyDown={allowsOnlyNumbers}
					min={0}
					step={1}
				/>
			</Styled.WrapperInputs>

			<Styled.WrapperInputs>
				<Form.PunchupSelect
					id="poolId"
					name="poolId"
					label={`${t('ticketDetails-pool')} (${t('optional')})`}
					options={poolOptions}
					withDefaultValue
				/>
				<Form.PunchupInput
					type="number"
					id="bundleSize"
					name="bundleSize"
					label={`${t('ticketDetails-bundle')} (${t('optional')})`}
					onKeyDown={allowsOnlyNumbers}
					min={0}
					step={1}
				/>
			</Styled.WrapperInputs>

			<Form.PunchupTextArea
				id="description"
				name="description"
				label={t('ticketDetails-ticket-description')}
				required={false}
			/>

			<PunchupDateTimePicker id="sellingTime" name="sellingTime" label={t('createEvent-tickets-onSale')} />

			<PunchupDateTimePicker
				id="offSellingTime"
				name="offSellingTime"
				label={t('createEvent-tickets-offSale')}
				required={false}
			/>

			<FlexContainer gap="12px" flexDirection="column">
				<TicketTagsField ticketTags={activeTicket?.tags} ticketTypeId={activeTicket?.ID} />

				<PunchupToggle
					htmlFor="ticketVisibility"
					name="ticketVisibility"
					label={t('ticketDetails-ticket-visbility')}
					info={t('ticketDetails-ticket-visbility-info')}
				/>

				<PunchupToggle
					htmlFor="alwaysShowTickets"
					name="alwaysShowTickets"
					label={t('ticketDetails-ticket-alwaysShowTickets')}
					info={t('ticketDetails-ticket-alwaysShowTickets-info')}
				/>

				<FlexContainer flexDirection="column">
					<PunchupToggle
						htmlFor="privateLink"
						name="privateLink"
						label={t('ticketDetails-ticket-privateLink')}
						info={t('ticketDetails-ticket-privateLink-info')}
					/>

					{privateLink && hiddenCode && (
						<Styled.WidgetUrlContainer>
							<Styled.WidgetUrlLabel>Tickets URL:</Styled.WidgetUrlLabel>
							<Styled.WidgetUrlWrapper>
								<Styled.WidgetUrl>{getWidgetUrl(hiddenCode)}</Styled.WidgetUrl>
								<PunchupButton
									type="button"
									variant="secondary"
									text={copiedToClipboard ? 'Copied!' : 'Copy to clipboard'}
									onClick={() => handleCopyToClipboard(getWidgetUrl(hiddenCode))}
								/>
							</Styled.WidgetUrlWrapper>
						</Styled.WidgetUrlContainer>
					)}
				</FlexContainer>

				<FlexContainer flexDirection="column">
					<PunchupToggle
						htmlFor="showImage"
						name="showImage"
						label={t('ticketDetails-ticket-showImage')}
						info={t('ticketDetails-ticket-showImage-info')}
					/>

					{showImage && (
						<Styled.ImageSection>
							{hasValidImage ? (
								<Styled.ImageContainer>
									<TicketImageCard
										isEdit={false}
										isCard={false}
										image={handleImageDisplay()}
										name={watch('name')}
										displayType={watch('displayType')}
									/>
									<Styled.ImageOptions>
										<PunchupButton type="button" variant="secondary" text="Replace" onClick={handleReplaceImage} />
										<PunchupButton type="button" variant="secondary" text="Delete" onClick={handleDeleteImage} />
									</Styled.ImageOptions>
								</Styled.ImageContainer>
							) : (
								<Styled.UploadContainer>
									<PunchupButton type="button" variant="primary" text="Upload Image" onClick={handleImageUpload} />
								</Styled.UploadContainer>
							)}
						</Styled.ImageSection>
					)}
				</FlexContainer>
			</FlexContainer>

			<SeatsCategories
				eventKey={event?.seatsEventKey}
				chartKey={event?.seatsChartKey}
				selectedCategory={seatsCategoryKey}
				onCategorySelect={handleCategorySelect}
			/>

			{!isNewTicket && (
				<Styled.DangerZone>
					<Styled.DangerZoneTitle>Danger Zone</Styled.DangerZoneTitle>
					<Styled.DangerZoneContent>
						<Styled.DangerZoneText>
							Delete this ticket type permanently. This action cannot be undone.
							<Styled.WarningText hidden={!hasSoldTickets}>
								⚠️ This ticket type has {activeTicket?.sold} tickets sold. All these sales will become invalid at the
								moment of scanning.
							</Styled.WarningText>
						</Styled.DangerZoneText>
						<Styled.DangerZoneButtons>
							{!showDeleteConfirm ? (
								<PunchupButton
									type="button"
									variant={PRIMARY}
									text="Delete"
									onClick={() => setShowDeleteConfirm(true)}
								/>
							) : (
								<>
									<PunchupButton type="button" variant={SECONDARY} text="✕" onClick={handleCancelDelete} />
									<PunchupButton type="button" variant={PRIMARY} text="Confirm" onClick={handleDelete} />
								</>
							)}
						</Styled.DangerZoneButtons>
					</Styled.DangerZoneContent>
				</Styled.DangerZone>
			)}
		</>
	);
};

export { LeftForm };
